import { Box }               from '@chakra-ui/react';
import { Text }              from '@chakra-ui/react';
import AccountCircleIcon     from '@/assets/icons/AccountCircleIcon';
import CropperModal          from '@/components/modal/CropperModal';
import DeleteIcon            from '@/assets/icons/DeleteIcon';
import React                 from 'react';
import { FileUploadRoot }    from '@/components/ui/file-button';

import { FileUpload }        from '@chakra-ui/react';
import { useTranslation }    from 'react-i18next';

interface IImagesSectionProps {
	logo: File | null;
	gallery: File[];
	existingLogoUrl: string | null;
	existingGalleryUrls: string[];
	onLogoChange: (file: File | null) => void;
	onGalleryChange: (gallery: File[]) => void;
	onExistingLogoDelete: () => void;
	onExistingGalleryDelete: (index: number) => void;
	isDisabled?: boolean;
}

type CropTarget = 'logo' | 'gallery';

const ImageDropzone = ({ onAccept, width, height }: { onAccept: (file: File) => void; width: string; height: string }) => {
	const { t } = useTranslation();
	return (
		<FileUploadRoot
			maxFiles={1}
			accept={['image/jpeg', 'image/png']}
			onFileAccept={({ files }) => { if (files[0]) onAccept(files[0]); }}
			width={width}
			flexShrink={0}
		>
			<FileUpload.Dropzone
				width={width}
				height={height}
				minH={height}
				maxH={height}
				border="2px dashed"
				borderColor="gray.300"
				borderRadius="md"
				display="flex"
				flexDirection="column"
				alignItems="center"
				justifyContent="center"
				cursor="pointer"
				gap="6px"
				bg="transparent"
				p={0}
				_hover={{ borderColor: 'gray.400' }}
			>
				<AccountCircleIcon color="gray.300" boxSize="48px" />
				<Text textStyle="xs" color="gray.500" lineHeight="1.4">
					{t('pages.editStand.form.images.logo.dropLabel')}
				</Text>
				<Text textStyle="xs" textDecoration="underline" color="gray.600" lineHeight="1.4">
					{t('pages.editStand.form.images.logo.importFile')}
				</Text>
			</FileUpload.Dropzone>
		</FileUploadRoot>
	);
};

const ImagePreview = ({ src, width, height, onDelete, showDelete }: { src: string; width: string; height: string; onDelete: () => void; showDelete: boolean }) => (
	<Box position="relative" width={width} height={height}>
		<img src={src} style={{ width, height, objectFit: 'cover', borderRadius: '6px' }} />
		{showDelete && (
			<Box
				as="button"
				position="absolute"
				top="6px"
				right="6px"
				backgroundColor="blackAlpha.700"
				borderRadius="full"
				width="25px"
				height="25px"
				padding="5px"
				cursor="pointer"
				display="flex"
				alignItems="center"
				justifyContent="center"
				_hover={{ backgroundColor: 'blackAlpha.900' }}
				onClick={onDelete}
			>
				<DeleteIcon fontSize="sm" color="white" />
			</Box>
		)}
	</Box>
);

const ImagesSection = ({ logo, gallery, existingLogoUrl, existingGalleryUrls, onLogoChange, onGalleryChange, onExistingLogoDelete, onExistingGalleryDelete, isDisabled }: IImagesSectionProps) => {
	const { t } = useTranslation();

	const [cropTarget, setCropTarget]       = React.useState<CropTarget | null>(null);
	const [pendingFile, setPendingFile]     = React.useState<File | null>(null);
	const [imageSource, setImageSource]     = React.useState<string>('');
	const [cropperIsOpen, setCropperIsOpen] = React.useState(false);

	React.useEffect(() => {
		if (pendingFile) {
			setImageSource(URL.createObjectURL(pendingFile));
		}
	}, [pendingFile]);

	const openCropper = (file: File, target: CropTarget) => {
		setPendingFile(file);
		setCropTarget(target);
		setCropperIsOpen(true);
	};

	const handleCropperSubmit = async (blob: File) => {
		const file = new File([blob], pendingFile?.name ?? blob.name, { type: blob.type || 'image/jpeg' });
		if (cropTarget === 'logo') {
			onLogoChange(file);
		} else if (cropTarget === 'gallery') {
			onGalleryChange([...gallery, file]);
		}
	};

	const logoPreview       = logo ? URL.createObjectURL(logo) : existingLogoUrl;
	const totalGalleryCount = existingGalleryUrls.length + gallery.length;

	return (
		<>
			<CropperModal
				imageSource={imageSource}
				imageType={pendingFile?.type || ''}
				isOpen={cropperIsOpen}
				setIsOpen={setCropperIsOpen}
				onSubmit={handleCropperSubmit}
				aspect={cropTarget === 'logo' ? 1 : 3 / 4}
			/>

			<Text className='poppins-bold' textStyle="lg" pt="60px" pb="20px">
				{t('pages.editStand.form.images.title')}
			</Text>

			{/* Logo */}
			<Text className='poppins-semibold' pb="8px" lineHeight="1.4">
				{t('pages.editStand.form.images.logo.title')}
			</Text>
			<Text textStyle="sm" color="gray.600" pb="15px" lineHeight="1.4">
				{t('pages.editStand.form.images.logo.description')}
			</Text>
			{logoPreview ? (
				<ImagePreview
					src={logoPreview}
					width="180px"
					height="180px"
					showDelete={!isDisabled}
					onDelete={() => logo ? onLogoChange(null) : onExistingLogoDelete()}
				/>
			) : !isDisabled ? (
				<ImageDropzone
					width="180px"
					height="180px"
					onAccept={file => openCropper(file, 'logo')}
				/>
			) : null}

			{/* Gallery */}
			<Text className='poppins-semibold' pt="30px" pb="8px" lineHeight="1.4">
				{t('pages.editStand.form.images.gallery.title')}
			</Text>
			<Text textStyle="sm" color="gray.600" pb="15px" lineHeight="1.4">
				{t('pages.editStand.form.images.gallery.description')}
			</Text>
			<Box display="flex" flexDirection="row" gap="15px" flexWrap="wrap">
				{existingGalleryUrls.map((url, index) => (
					<ImagePreview
						key={`existing-${index}`}
						src={url}
						width="180px"
						height="240px"
						showDelete={!isDisabled}
						onDelete={() => onExistingGalleryDelete(index)}
					/>
				))}
				{gallery.map((file, index) => (
					<ImagePreview
						key={`new-${index}`}
						src={URL.createObjectURL(file)}
						width="180px"
						height="240px"
						showDelete={!isDisabled}
						onDelete={() => onGalleryChange(gallery.filter((_, i) => i !== index))}
					/>
				))}
				{!isDisabled && totalGalleryCount < 3 && (
					<ImageDropzone
						width="180px"
						height="240px"
						onAccept={file => openCropper(file, 'gallery')}
					/>
				)}
			</Box>
		</>
	);
};

export default ImagesSection;
